/**
 * Spanish locale prefix used in public URLs (`/es`, `/es/...`).
 * Keep the inline Segment snippet in `app/[locale]/constants/segment.ts` aligned with this logic.
 */
export type SiteLocale = "en" | "es";

export type LocalePathInfo = {
  locale: SiteLocale;
  /** Path with `/es` prefix removed when applicable; suitable for route/analytics matching. */
  canonicalPath: string;
};

export function getLocaleAndCanonicalPath(pathname: string): LocalePathInfo {
  if (pathname === "/es" || pathname === "/es/") {
    return { locale: "es", canonicalPath: "/" };
  }
  if (pathname.startsWith("/es/")) {
    return { locale: "es", canonicalPath: pathname.slice("/es".length) };
  }
  return { locale: "en", canonicalPath: pathname };
}

/** True for `es`, `es-ES`, `es_ES`, and other es-prefixed BCP-47 tags. */
export function isSpanishLocale(locale: string): boolean {
  return /^es(?:[-_]|$)/i.test(locale);
}

/** Strip a leading `es/` prefix from a factory-style path (`es/bachelors/foo`). */
export function stripLocalePrefixFromPath(path: string): string {
  return path.replace(/^\/?es(?:\/|$)/, "");
}

/** Prefix a factory-style path with `es/` when the request locale is Spanish. */
export function prefixLocalePath(path: string, locale: string): string {
  const withoutLocale = stripLocalePrefixFromPath(path);
  if (isSpanishLocale(locale)) {
    return withoutLocale ? `es/${withoutLocale}` : "es";
  }
  return withoutLocale;
}
